import { action, observable, runInAction, toJS } from 'mobx';
import { ErrorInterface } from 'modules/core/api/globalApi';
import { NO_REGION } from '../../orders/constants/fias';
import controller from './controller';

/** Элемент справочника субъектов РФ (GET /v1/regions). */
export interface RegionItemInterface {
  id: string | number,
  name?: string,
}

/** Элемент списка справочников (GET /v1/ref). Готового интерфейса в api нет;
 *  id — число: refId валидируется как Yup.number().min(1), см. universal-reference/validation.js */
export interface RefItemInterface {
  id: number,
  name?: string,
}

/** Значение универсального справочника (GET /v1/ref_value/{refId}) — полиморфная форма:
 *  разные справочники несут разные поля (string1/int1/sort/...), сужать под каждый refId нельзя. */
export interface RefValueItemInterface {
  id: number | string,
  name?: string,
  int1?: number,
  string1?: string,
  sort?: number,
  [key: string]: unknown,
}

export interface UniversalReferenceStore {
  // список субъектов РФ; служебная опция «Без региона» (NO_REGION) подшита первой — 1:1 с бывшим Redux-экшеном
  subjectRF: RegionItemInterface[],
  // true — только после успешного ответа: при ошибке остаётся false, и следующий mount повторит запрос
  subjectRFLoaded: boolean,
  loadingSubjectRF: boolean,

  // список справочников; кэш на сессию (load-once)
  refs: RefItemInterface[],
  refsLoaded: boolean,

  // значения справочников — вечный кэш на сессию по String(refId): справочники меняются крайне редко.
  // Перезапись только через getRefValues(refId, true) — force-обновление из редактора справочников.
  // В Redux-коллекции refValuesCollection запись не обновлялась (append-only) — после правки в редакторе
  // селекторы до перезагрузки страницы показывали устаревшие значения; здесь это исправлено.
  refValuesByRefId: Record<string, RefValueItemInterface[]>,

  error: ErrorInterface,
}

const initialValues: UniversalReferenceStore = {
  subjectRF: [],
  subjectRFLoaded: false,
  loadingSubjectRF: false,

  refs: [],
  refsLoaded: false,

  refValuesByRefId: {},

  error: null,
};

export const universalReferenceStore = observable(initialValues);

// запросы в полёте (не обзерваблы: на рендер не влияют) — single-flight на дублирующие mount'ы.
// API возвращает данные, поэтому повторный вызов садится на тот же промис (piggyback), а не молчит.
let subjectRFInFlight = false;
let refsInFlight: Promise<RefItemInterface[]> | null = null;
const refValuesInFlight = new Map<string, Promise<RefValueItemInterface[]>>();

export const universalReferenceActions = {

  handleError: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      universalReferenceStore.error = {
        message: errorData?.message,
        errorCode: errorData?.errorCode,
        timestamp: errorData?.timestamp,
        status: response?.status,
        connectError: response?.connectError,
        isError: response?.error,
      };
      throw errorData;
    }
  }),

  clearError: action(() => {
    universalReferenceStore.error = null;
  }),

  // GET /v1/regions — субъекты РФ. Load-once: один запрос за сессию.
  // В Redux список качался заново на каждый mount RegionSelector (гейта не было).
  async getSubjectRF() {
    if (universalReferenceStore.subjectRFLoaded || subjectRFInFlight) {
      return;
    }
    subjectRFInFlight = true;
    runInAction(() => {
      universalReferenceStore.loadingSubjectRF = true;
    });
    try {
      const response = await controller.getSubjectRF();

      universalReferenceActions.handleError(response);
      runInAction(() => {
        universalReferenceStore.subjectRFLoaded = true;
        // ведущий пробел в « Без региона» — 1:1 с бывшим Redux-экшеном
        universalReferenceStore.subjectRF = [{ id: NO_REGION, name: ' Без региона' }, ...response?.data ?? []];
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      subjectRFInFlight = false;
      runInAction(() => {
        universalReferenceStore.loadingSubjectRF = false;
      });
    }
  },

  // GET /v1/ref — список справочников. Кэш на сессию: один запрос за все mount'ы RefSelector
  // и редактора (в Redux гейта не было — каждый mount перезапрашивал).
  // force=true перечитывает список (нужен редактору после CRUD справочников).
  // Возвращает копию (toJS), а не массив стора: потребители исторически ждут данные из
  // промиса, а не подпиской, и иногда сортируют ответ (PriceCalculationGrid) — мутировать
  // кэш стора нельзя (в Redux такая сортировка молча портила общее состояние).
  // При ошибке возвращает [] и не фиксирует кэш — следующий mount повторит запрос.
  async getRefs(force = false): Promise<RefItemInterface[]> {
    if (!force && universalReferenceStore.refsLoaded) {
      return toJS(universalReferenceStore.refs);
    }
    if (refsInFlight) {
      return refsInFlight;
    }

    refsInFlight = (async () => {
      try {
        const response = await controller.getRef();

        universalReferenceActions.handleError(response);
        const refs = response?.data ?? [];
        runInAction(() => {
          universalReferenceStore.refsLoaded = true;
          universalReferenceStore.refs = refs;
        });
        return toJS(refs);
      } catch {
        return [];
      } finally {
        refsInFlight = null;
      }
    })();

    return refsInFlight;
  },

  // GET /v1/ref_value/{refId} — значения справочника. Кэш на сессию по refId (строка|число
  // нормализуются в один ключ: в Redux '27' и 27 были разными записями коллекции).
  // force=true (бывш. alwaysTakeFromDB редактора) перечитывает и ЗАМЕНЯЕТ запись кэша.
  // Как и getRefs, возвращает toJS-копию — кэш стора потребители мутировать не могут.
  // При ошибке — [] без записи в кэш.
  async getRefValues(refId: number | string, force = false): Promise<RefValueItemInterface[]> {
    const key = String(refId);
    if (!force && Object.prototype.hasOwnProperty.call(universalReferenceStore.refValuesByRefId, key)) {
      return toJS(universalReferenceStore.refValuesByRefId[key]);
    }
    const inFlight = refValuesInFlight.get(key);
    if (inFlight) {
      return inFlight;
    }

    const promise = (async () => {
      try {
        const response = await controller.getRefValue({ refId });

        universalReferenceActions.handleError(response);
        const refValues = response?.data ?? [];
        runInAction(() => {
          universalReferenceStore.refValuesByRefId[key] = refValues;
        });
        return toJS(refValues);
      } catch {
        return [];
      } finally {
        refValuesInFlight.delete(key);
      }
    })();

    refValuesInFlight.set(key, promise);
    return promise;
  },

  // CRUD справочников и их значений (POST/PUT/DELETE /v1/ref и /v1/ref_value) —
  // редактор универсальных справочников. Кэши refs/refValuesByRefId здесь не трогаем:
  // после CRUD редактор перечитывает их через getRefs(true)/getRefValues(refId, true).
  // При ошибке handleError запишет error в стор и бросит исключение — модалка
  // редактора показывает toastr и пишет ошибки в форму (бывш. dispatchFail Redux-экшенов).
  async createRef(params: Record<string, unknown>): Promise<void> {
    const response = await controller.createRef(params);
    universalReferenceActions.handleError(response);
  },

  async updateRef(params: Record<string, unknown>): Promise<void> {
    const response = await controller.updateRef(params);
    universalReferenceActions.handleError(response);
  },

  async deleteRef(params: { id: number | string }): Promise<void> {
    const response = await controller.deleteRef(params);
    universalReferenceActions.handleError(response);
  },

  async createRefValue(params: Record<string, unknown>): Promise<void> {
    const response = await controller.createRefValue(params);
    universalReferenceActions.handleError(response);
  },

  async updateRefValue(params: Record<string, unknown>): Promise<void> {
    const response = await controller.updateRefValue(params);
    universalReferenceActions.handleError(response);
  },

  async deleteRefValue(params: { id: number | string }): Promise<void> {
    const response = await controller.deleteRefValue(params);
    universalReferenceActions.handleError(response);
  },

};
